import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { MagnifyingGlass, Package } from "@phosphor-icons/react";

/**
 * Debounced autocomplete input backed by /api/material-catalog.
 * Shows a floating suggestion list under the input as the user types.
 * On select: bubbles the whole catalog item back so the parent can pre-fill
 * label + unit_price (and optionally category).
 *
 * Props:
 *   value        — free-text current label value
 *   onChange     — (label: string) => void, called on every keystroke
 *   onSelect     — (item: {ref, name, unit_price, category}) => void, called when a suggestion is clicked
 *   placeholder  — text placeholder
 *   testid       — data-testid prefix
 *   className    — extra classes for the input
 *   showPrice    — legacy prop, kept for backwards compat but ignored (no prices in UI)
 */
export default function MaterialCatalogAutocomplete({
  value = "",
  onChange,
  onSelect,
  placeholder = "Article — commence à taper pour rechercher…",
  testid = "material-autocomplete",
  className = "",
  showPrice: _showPrice = false, // legacy prop, ignored — no prices displayed anymore
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const debounceRef = useRef(null);
  const rootRef = useRef(null);

  // Debounced search
  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/material-catalog", { params: { q, limit: 30 } });
        setSuggestions(r.data);
        setOpen(r.data.length > 0);
        setHighlight(0);
      } catch {
        setSuggestions([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const pick = (item) => {
    onChange(item.name);
    onSelect && onSelect(item);
    setOpen(false);
    setSuggestions([]);
  };

  const onKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && suggestions[highlight]) { e.preventDefault(); pick(suggestions[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => (value || "").trim().length >= 2 && suggestions.length > 0 && setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          className={`w-full h-10 bg-white/5 border border-white/10 rounded-md pl-7 pr-3 text-sm focus:outline-none focus:border-[#005CFF]/50 ${className}`}
          data-testid={testid}
        />
      </div>
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-40 left-0 right-0 mt-1 rounded-xl bg-[#0A0A0A] border border-white/15 shadow-2xl max-h-64 overflow-y-auto"
          data-testid={`${testid}-suggestions`}
        >
          {loading && (
            <div className="px-3 py-2 text-[11px] text-white/40">Recherche…</div>
          )}
          {suggestions.map((item, idx) => (
            <button
              type="button"
              key={item.id || item.ref}
              onClick={() => pick(item)}
              onMouseEnter={() => setHighlight(idx)}
              className={`w-full text-left px-3 py-2 flex items-start gap-2 border-b border-white/5 last:border-b-0 ${
                idx === highlight ? "bg-[#005CFF]/15" : "hover:bg-white/5"
              }`}
              data-testid={`${testid}-item-${item.id || idx}`}
            >
              <span className="w-6 h-6 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0 mt-0.5">
                <Package size={11} className="text-emerald-400" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm truncate">{item.name}</span>
                <span className="block text-[10px] text-white/40 truncate">
                  {item.ref && <>Réf {item.ref} · </>}
                  {item.category}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
